import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Input,
    Label,
    ListBox,
    Select,
    Switch,
    TextField,
} from '@heroui/react';
import { FormDrawer } from '@/components/formDrawer';
import type { AttributeInputKind } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { ATTRIBUTE_INPUT_KINDS } from '../attributesMeta';
import type { useAttributeTypeForm } from './useAttributeTypeForm';

type AttributeTypeFormModalViewProps = {
    error: string | null;
    form: ReturnType<typeof useAttributeTypeForm>;
    isOpen: boolean;
    isSubmitting: boolean;
    mode: 'create' | 'edit';
    onClose: () => void;
};

export const AttributeTypeFormModalView: React.FC<
    AttributeTypeFormModalViewProps
> = ({ error, form, isOpen, isSubmitting, mode, onClose }) => {
    const { t } = useTranslation('pages');
    const {
        name,
        slug,
        inputKind,
        isMultiSelect,
        isValid,
        supportsMultiSelect,
        onInputKindChange,
        onMultiSelectChange,
        onNameChange,
        onSlugChange,
        onSubmit,
    } = form;

    const copy = React.useMemo(
        () => ({
            title:
                mode === 'edit'
                    ? t('attributes.components.dictionary.typeForm.editTitle')
                    : t('attributes.components.dictionary.typeForm.title'),
            description: t(
                'attributes.components.dictionary.typeForm.description',
            ),
            nameLabel: t('attributes.components.dictionary.typeForm.nameLabel'),
            namePlaceholder: t(
                'attributes.components.dictionary.typeForm.namePlaceholder',
            ),
            slugLabel: t('attributes.components.dictionary.typeForm.slugLabel'),
            slugPlaceholder: t(
                'attributes.components.dictionary.typeForm.slugPlaceholder',
            ),
            inputKindLabel: t(
                'attributes.components.dictionary.typeForm.inputKindLabel',
            ),
            multiSelectLabel: t(
                'attributes.components.dictionary.typeForm.multiSelectLabel',
            ),
            multiSelectHint: t(
                'attributes.components.dictionary.typeForm.multiSelectHint',
            ),
            cancel: t('attributes.components.dictionary.typeForm.cancel'),
            submit:
                mode === 'edit'
                    ? t('attributes.components.dictionary.typeForm.editSubmit')
                    : t('attributes.components.dictionary.typeForm.submit'),
            inputKinds: {
                Select: t('attributes.components.dictionary.inputKinds.Select'),
                Text: t('attributes.components.dictionary.inputKinds.Text'),
                Number: t('attributes.components.dictionary.inputKinds.Number'),
                Boolean: t(
                    'attributes.components.dictionary.inputKinds.Boolean',
                ),
            },
        }),
        [mode, t],
    );

    return (
        <FormDrawer
            footer={
                <>
                    <Button
                        isDisabled={isSubmitting}
                        onPress={onClose}
                        variant="tertiary"
                        {...getNodeTestIdProp('attribute-type-form-cancel')}
                    >
                        {copy.cancel}
                    </Button>
                    <Button
                        isDisabled={!isValid}
                        isPending={isSubmitting}
                        onPress={onSubmit}
                        variant="primary"
                        {...getNodeTestIdProp('attribute-type-form-submit')}
                    >
                        {copy.submit}
                    </Button>
                </>
            }
            isOpen={isOpen}
            onClose={onClose}
            subtitle={copy.description}
            testId="attribute-type-form"
            title={copy.title}
        >
            <TextField
                isRequired
                onChange={onNameChange}
                value={name}
                {...getNodeTestIdProp('attribute-type-form-name')}
            >
                <Label>{copy.nameLabel}</Label>
                <Input placeholder={copy.namePlaceholder} />
            </TextField>
            <TextField
                isRequired
                onChange={onSlugChange}
                value={slug}
                {...getNodeTestIdProp('attribute-type-form-slug')}
            >
                <Label>{copy.slugLabel}</Label>
                <Input placeholder={copy.slugPlaceholder} />
            </TextField>
            <Select
                fullWidth
                onChange={(value) =>
                    onInputKindChange(value as AttributeInputKind)
                }
                value={inputKind}
                variant="secondary"
            >
                <Label>{copy.inputKindLabel}</Label>
                <Select.Trigger
                    {...getNodeTestIdProp('attribute-type-form-kind')}
                >
                    <Select.Value />
                    <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                    <ListBox>
                        {ATTRIBUTE_INPUT_KINDS.map((kind) => (
                            <ListBox.Item
                                id={kind}
                                key={kind}
                                textValue={copy.inputKinds[kind]}
                            >
                                {copy.inputKinds[kind]}
                                <ListBox.ItemIndicator />
                            </ListBox.Item>
                        ))}
                    </ListBox>
                </Select.Popover>
            </Select>
            <div className="flex flex-col gap-1.5">
                <Switch
                    isDisabled={!supportsMultiSelect}
                    isSelected={isMultiSelect}
                    onChange={onMultiSelectChange}
                    {...getNodeTestIdProp('attribute-type-form-multiselect')}
                >
                    <Switch.Control>
                        <Switch.Thumb />
                    </Switch.Control>
                    <Switch.Content>
                        <Label className="text-sm">
                            {copy.multiSelectLabel}
                        </Label>
                    </Switch.Content>
                </Switch>
                <p className="text-xs text-muted">{copy.multiSelectHint}</p>
            </div>
            {error ? (
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp('attribute-type-form-error')}
                >
                    {error}
                </p>
            ) : null}
        </FormDrawer>
    );
};
